import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import Login from './Login';
import IntakeForm from './IntakeForm';
import IntakeFormViewer from './IntakeFormViewer';
import AdminDashboard from './AdminDashboard';
import Contact from './Contact';
import ContractPage from './ContractPage';
import PaymentPage from './PaymentPage';
import API_BASE_URL, { API_ENDPOINTS } from './config/api';
import { printAgreementPdf } from './utils/printAgreement';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase/config';

/* ── Inline SVG icon set ── */
const IconDocument = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <line x1="10" y1="9" x2="8" y2="9"/>
  </svg>
);
const IconCard = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2"/>
    <line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);
const IconClipboard = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
    <rect x="9" y="3" width="6" height="4" rx="2"/>
    <path d="M9 12l2 2 4-4"/>
  </svg>
);
const IconCalendar = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

function LoadingScreen() {
  return (
    <div className="app-loading-screen">
      <img src="/NextLeaderLogo_Web.png" alt="NextLeader Coaching" className="loading-logo" />
      <p className="loading-text">Loading your portal…</p>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setAuthLoading(false);
        return;
      }
      try {
        const token = await firebaseUser.getIdToken();
        const [profileRes, intakeRes] = await Promise.all([
          fetch(API_ENDPOINTS.AUTH_PROFILE, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(API_ENDPOINTS.INTAKE_STATUS, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (!profileRes.ok) {
          await auth.signOut();
          setAuthLoading(false);
          return;
        }
        const userData = (await profileRes.json()).user;
        const intakeData = intakeRes.ok ? await intakeRes.json() : { hasCompletedIntake: false };
        setUser({
          email: firebaseUser.email,
          name: userData.firstName && userData.lastName
            ? `${userData.firstName} ${userData.lastName}`
            : firebaseUser.displayName || 'User',
          uid: firebaseUser.uid,
          userData,
          hasCompletedIntake: intakeData.hasCompletedIntake,
          hasSignedContract: userData.hasSignedContract || false,
          hasPaid: userData.hasPaid || false,
          paymentMethod: userData.paymentMethod || null,
          isAdmin: userData.isAdmin || false,
          token,
        });
        // No navigate() here — the URL the user reloaded on is already correct
      } catch {
        await auth.signOut();
      } finally {
        setAuthLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    navigate(userData.isAdmin ? '/admin' : '/dashboard', { replace: true });
  };

  const handleLogout = async () => {
    await auth.signOut();
    setUser(null);
    navigate('/login', { replace: true });
  };

  const handleContractSigned = () => {
    setUser(prev => ({ ...prev, hasSignedContract: true }));
    navigate('/dashboard', { replace: true });
  };

  const handlePaymentSent = (method = 'other') => {
    setUser(prev => ({ ...prev, hasPaid: true, paymentMethod: method }));
    navigate('/dashboard', { replace: true });
  };

  const handleIntakeComplete = () => {
    setUser(prev => ({ ...prev, hasCompletedIntake: true }));
    navigate('/dashboard', { replace: true });
  };

  if (authLoading) return <LoadingScreen />;

  return (
    <Routes>
      {/* Public route */}
      <Route
        path="/login"
        element={
          user
            ? <Navigate to={user.isAdmin ? '/admin' : '/dashboard'} replace />
            : <Login onLogin={handleLogin} />
        }
      />

      {/* All authenticated routes */}
      <Route
        path="/*"
        element={
          !user
            ? <Navigate to="/login" replace />
            : (
              <AuthenticatedRoutes
                user={user}
                setUser={setUser}
                onLogout={handleLogout}
                onContractSigned={handleContractSigned}
                onPaymentSent={handlePaymentSent}
                onIntakeComplete={handleIntakeComplete}
              />
            )
        }
      />

      {/* Root redirect */}
      <Route path="/" element={<Navigate to={user ? (user.isAdmin ? '/admin' : '/dashboard') : '/login'} replace />} />
    </Routes>
  );
}

function AuthenticatedRoutes({ user, setUser, onLogout, onContractSigned, onPaymentSent, onIntakeComplete }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Contract gate: force to /contract until signed (skip if already there)
  if (!user.hasSignedContract && location.pathname !== '/contract') {
    return <Navigate to="/contract" replace />;
  }

  return (
    <Routes>
      <Route
        path="/contract"
        element={<ContractPage user={user} onSigned={onContractSigned} />}
      />

      <Route
        path="/admin"
        element={
          user.isAdmin
            ? <AdminDashboard adminData={{ ...user, token: user.token || 'temp-token' }} onLogout={onLogout} />
            : <Navigate to="/dashboard" replace />
        }
      />

      <Route
        path="/contact"
        element={<Contact onBack={() => navigate('/dashboard')} />}
      />

      <Route
        path="/payment"
        element={<PaymentPage user={user} onPaymentSent={onPaymentSent} hideLater onBack={() => navigate('/dashboard')} onLogout={onLogout} />}
      />

      <Route
        path="/intake"
        element={
          <IntakeForm
            user={user}
            onComplete={onIntakeComplete}
            onExit={() => navigate('/dashboard')}
          />
        }
      />

      <Route
        path="/intake/view"
        element={
          <div className="App user-dashboard">
            <header className="dashboard-header">
              <div className="header-brand">
                <a href="https://www.nextleadercoaching.com" target="_blank" rel="noopener noreferrer">
                  <img src="/NextLeaderLogo_Web.png" alt="NextLeader Coaching" className="header-logo" />
                </a>
                <div className="header-brand-divider" />
                <h1>My Intake Form</h1>
              </div>
              <div className="header-actions">
                <button type="button" className="header-link-btn" onClick={() => navigate('/dashboard')}>
                  ← Dashboard
                </button>
                <button type="button" className="logout-btn" onClick={onLogout}>Sign Out</button>
              </div>
            </header>
            <div className="client-intake-view">
              <IntakeFormViewer user={user} onBack={() => navigate('/dashboard')} inline={true} />
            </div>
            <footer className="dashboard-footer" role="contentinfo">
              <a href="https://www.nextleadercoaching.com" target="_blank" rel="noopener noreferrer" className="footer-brand-link">
                <img src="/NextLeaderLogo_Web.png" alt="NextLeader Coaching" className="footer-logo" />
              </a>
              <p>&copy; {new Date().getFullYear()} NextLeader Coaching. All rights reserved.</p>
            </footer>
          </div>
        }
      />

      <Route
        path="/dashboard"
        element={
          <div className="App user-dashboard">
            <header className="dashboard-header">
              <div className="header-brand">
                <a href="https://www.nextleadercoaching.com" target="_blank" rel="noopener noreferrer">
                  <img src="/NextLeaderLogo_Web.png" alt="NextLeader Coaching" className="header-logo" />
                </a>
                <div className="header-brand-divider" />
                <h1>My Dashboard</h1>
              </div>
              <div className="header-actions">
                <span className="welcome-text">Welcome, {user.name}</span>
                <button type="button" className="header-link-btn" onClick={() => navigate('/contact')}>
                  Contact
                </button>
                {user.isAdmin && (
                  <button type="button" className="admin-btn" onClick={() => navigate('/admin')}>
                    Admin
                  </button>
                )}
                <button type="button" className="logout-btn" onClick={onLogout}>Sign Out</button>
              </div>
            </header>

            <ClientHome
              user={user}
              onViewIntakeForm={() => navigate('/intake/view')}
              onFillIntake={() => navigate('/intake')}
              onPayNow={() => navigate('/payment')}
            />

            <footer className="dashboard-footer" role="contentinfo">
              <a href="https://www.nextleadercoaching.com" target="_blank" rel="noopener noreferrer" className="footer-brand-link">
                <img src="/NextLeaderLogo_Web.png" alt="NextLeader Coaching" className="footer-logo" />
              </a>
              <p>&copy; {new Date().getFullYear()} NextLeader Coaching. All rights reserved.</p>
            </footer>
          </div>
        }
      />

      {/* Default: redirect to dashboard (or admin) */}
      <Route path="/" element={<Navigate to={user.isAdmin ? '/admin' : '/dashboard'} replace />} />
      <Route path="*" element={<Navigate to={user.isAdmin ? '/admin' : '/dashboard'} replace />} />
    </Routes>
  );
}

function ClientHome({ user, onViewIntakeForm, onFillIntake, onPayNow }) {
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [htmlAgreement, setHtmlAgreement] = useState(null);
  const [contractLoading, setContractLoading] = useState(true);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [hasScheduled, setHasScheduled] = useState(false);

  const paymentMethod = user.paymentMethod || user.userData?.paymentMethod;
  const paymentConfirmed = ['stripe', 'venmo', 'zelle'].includes(paymentMethod);
  const paymentAmount = user.userData?.contractTerms?.paymentAmount;
  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

  const checkSessionStatus = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/booking/my-sessions`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const sessions = Array.isArray(data) ? data : (data.sessions || []);
        setHasScheduled(sessions.length > 0);
      }
    } catch {
      // no-op
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/contracts/my`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setContract(data.contracts[0] || null);
          setHtmlAgreement(data.htmlAgreement || null);
        }
      } catch {
        // no-op
      } finally {
        setContractLoading(false);
      }

      await checkSessionStatus();
    };
    fetchData();
  }, [user.token]);

  // Re-check session status when the user tabs back (e.g. after booking via Calendly)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkSessionStatus();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user.token]);

  const handleViewContract = async () => {
    if (!contract) return;
    setFileLoading(true);
    setFileError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/contracts/my/${contract._id}/file`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        window.open(data.fileUrl, '_blank', 'noopener,noreferrer');
      } else {
        setFileError('Could not load the contract file. Please try again later.');
      }
    } catch {
      setFileError('Could not load the contract file. Please try again later.');
    } finally {
      setFileLoading(false);
    }
  };

  const hasFile = contract && contract.fileName;
  const firstSessionDone = user.userData?.firstSessionBooked || hasScheduled;
  const activeStep = !paymentConfirmed ? 'payment'
    : !user.hasCompletedIntake ? 'intake'
    : !firstSessionDone ? 'session'
    : null;

  const firstName = user.name?.split(' ')[0] || user.name;
  const stepHints = {
    payment: 'Complete your payment below to unlock your intake form and first session.',
    intake:  'Fill out your intake form so your coach can personalize every session.',
    session: 'Book your first coaching session to begin your engagement.',
  };

  return (
    <main className="client-home">

      {/* ── HERO ── */}
      <section className="client-hero">
        <p className="client-eyebrow">NextLeader Coaching</p>
        <h1 className="client-hero__title">Welcome back, {firstName}.</h1>
        <p className="client-hero__sub">Your coaching portal — track your progress and take your next step.</p>
      </section>

      {/* ── PROGRESS ── */}
      <section className="client-section client-section--alt">
        <div className="client-container">
          <p className="section-eyebrow">Enrollment Progress</p>
          <h2 className="client-section__h2">{activeStep ? 'Your next step' : "You're all set"}</h2>

          <div className="dashboard-checklist">
            <div
              className={`checklist-item checklist-done${(htmlAgreement || hasFile) ? ' checklist-clickable' : ''}`}
              onClick={(htmlAgreement || hasFile) ? () => setShowAgreementModal(true) : undefined}
              role={(htmlAgreement || hasFile) ? 'button' : undefined}
              tabIndex={(htmlAgreement || hasFile) ? 0 : undefined}
              onKeyDown={(htmlAgreement || hasFile) ? (e) => e.key === 'Enter' && setShowAgreementModal(true) : undefined}
              title={(htmlAgreement || hasFile) ? 'View your coaching agreement' : undefined}
            >
              <div className="checklist-dot"><IconCheck /></div>
              <span className="checklist-label">Agreement</span>
            </div>
            <div className="checklist-connector checklist-connector-done" />

            <div
              className={`checklist-item checklist-clickable ${paymentConfirmed ? 'checklist-done' : activeStep === 'payment' ? 'checklist-active' : 'checklist-pending'}`}
              onClick={onPayNow}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && onPayNow()}
              title={paymentConfirmed ? 'View payment' : 'Complete your payment'}
            >
              <div className="checklist-dot">{paymentConfirmed ? <IconCheck /> : activeStep === 'payment' ? '!' : '2'}</div>
              <span className="checklist-label">{paymentConfirmed ? 'Payment' : 'Payment Due'}</span>
            </div>
            <div className={`checklist-connector${paymentConfirmed ? ' checklist-connector-done' : ''}`} />

            <div
              className={`checklist-item checklist-clickable ${user.hasCompletedIntake ? 'checklist-done' : activeStep === 'intake' ? 'checklist-active' : 'checklist-pending'}`}
              onClick={user.hasCompletedIntake ? onViewIntakeForm : onFillIntake}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && (user.hasCompletedIntake ? onViewIntakeForm() : onFillIntake())}
              title={user.hasCompletedIntake ? 'View your intake form' : 'Complete your intake form'}
            >
              <div className="checklist-dot">{user.hasCompletedIntake ? <IconCheck /> : '3'}</div>
              <span className="checklist-label">Intake Form</span>
            </div>
            <div className={`checklist-connector${user.hasCompletedIntake ? ' checklist-connector-done' : ''}`} />

            <div
              className={`checklist-item checklist-clickable ${firstSessionDone ? 'checklist-done' : activeStep === 'session' ? 'checklist-active' : 'checklist-pending'}`}
              onClick={() => window.open('https://calendly.com/nextleadercoaching/coaching-session', '_blank', 'noopener,noreferrer')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && window.open('https://calendly.com/nextleadercoaching/coaching-session', '_blank', 'noopener,noreferrer')}
              title={firstSessionDone ? 'Schedule another session' : 'Schedule your first session'}
            >
              <div className="checklist-dot">{firstSessionDone ? <IconCheck /> : '4'}</div>
              <span className="checklist-label">First Session</span>
            </div>
          </div>

          {activeStep && stepHints[activeStep] && (
            <p className="client-step-hint">{stepHints[activeStep]}</p>
          )}
        </div>
      </section>

      {/* ── PORTAL CARDS ── */}
      <section className="client-section">
        <div className="client-container">
          <p className="section-eyebrow">Your Coaching Portal</p>
          <h2 className="client-section__h2">Everything in one place</h2>

          <div className="client-links">

            {/* Card 1: My Agreement */}
            <div className="client-link-card card-done">
              <div className="client-link-icon"><IconDocument /></div>
              <h2>My Agreement</h2>
              {contractLoading ? (
                <p className="client-link-status">Loading…</p>
              ) : hasFile ? (
                <>
                  <span className="client-card-badge badge-done">Signed</span>
                  <div className="card-action-row">
                    <button type="button" className="client-link-btn" onClick={handleViewContract} disabled={fileLoading}>
                      {fileLoading ? 'Opening…' : 'View Agreement'}
                    </button>
                    {fileError && <p className="client-link-error">{fileError}</p>}
                  </div>
                </>
              ) : htmlAgreement ? (
                <>
                  <span className="client-card-badge badge-done">
                    Signed{htmlAgreement.signedDate ? ` · ${new Date(htmlAgreement.signedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                  </span>
                  <div className="card-action-row">
                    <button type="button" className="client-link-btn" onClick={() => setShowAgreementModal(true)}>
                      View Agreement
                    </button>
                  </div>
                </>
              ) : (
                <p className="client-link-status">No agreement on file yet.</p>
              )}
            </div>

            {/* Card 2: My Payment */}
            <div className={`client-link-card ${paymentConfirmed ? 'card-done' : 'card-action'}`}>
              <div className="client-link-icon"><IconCard /></div>
              <h2>My Payment</h2>
              {paymentConfirmed ? (
                <>
                  <span className="client-card-badge badge-done">Payment Received</span>
                  <p className="client-link-desc">Paid in full</p>
                  <div className="card-action-row">
                    <button type="button" className="client-link-btn" onClick={onPayNow}>View Receipt</button>
                  </div>
                </>
              ) : (
                <>
                  <span className="client-card-badge badge-action">Payment Due</span>
                  {paymentAmount && <p className="client-link-desc">{fmt(paymentAmount)} due before your first session</p>}
                  <div className="card-action-row">
                    <button type="button" className="client-link-btn" onClick={onPayNow}>Complete Payment</button>
                  </div>
                </>
              )}
            </div>

            {/* Card 3: My Intake Form */}
            <div className={`client-link-card ${user.hasCompletedIntake ? 'card-done' : 'card-action'}`}>
              <div className="client-link-icon"><IconClipboard /></div>
              <h2>My Intake Form</h2>
              {user.hasCompletedIntake ? (
                <>
                  <span className="client-card-badge badge-done">Submitted</span>
                  <div className="card-action-row">
                    <button type="button" className="client-link-btn" onClick={onViewIntakeForm}>View Form</button>
                  </div>
                </>
              ) : (
                <>
                  <span className="client-card-badge badge-action">Action Required</span>
                  <p className="client-link-desc">Share your background and goals so your coach can personalize every session.</p>
                  <div className="card-action-row">
                    <button type="button" className="client-link-btn" onClick={onFillIntake}>Complete Form</button>
                  </div>
                </>
              )}
            </div>

            {/* Card 4: First Session */}
            <div className={`client-link-card ${firstSessionDone ? 'card-done' : ''}`}>
              <div className="client-link-icon"><IconCalendar /></div>
              <h2>First Session</h2>
              {firstSessionDone ? (
                <>
                  <span className="client-card-badge badge-done">Scheduled</span>
                  <p className="client-link-desc">Your session is confirmed. Check your email for details.</p>
                  <div className="card-action-row">
                    <button
                      type="button"
                      className="client-link-btn"
                      onClick={() => window.open('https://calendly.com/nextleadercoaching/coaching-session', '_blank', 'noopener,noreferrer')}
                    >
                      Book Another
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="client-link-desc">Book your first coaching session to begin your engagement.</p>
                  <div className="card-action-row">
                    <button
                      type="button"
                      className="client-link-btn"
                      onClick={() => window.open('https://calendly.com/nextleadercoaching/coaching-session', '_blank', 'noopener,noreferrer')}
                    >
                      Schedule Session
                    </button>
                  </div>
                </>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* ── Agreement modal ── */}
      {showAgreementModal && htmlAgreement && (
        <div className="modal-overlay" onClick={() => setShowAgreementModal(false)}>
          <div className="modal modal-large agreement-view-modal" onClick={e => e.stopPropagation()}>
            <div className="agreement-modal-header">
              <h4>Coaching Agreement</h4>
              <button className="close-btn" onClick={() => setShowAgreementModal(false)}>&times;</button>
            </div>
            <div className="agreement-modal-body">
              <div dangerouslySetInnerHTML={{ __html: htmlAgreement.html }} />
            </div>
            <div className="agreement-signature-block">
              <div className="agreement-sig-party">
                <p className="agreement-sig-label">NextLeader Coaching (Coach)</p>
                <p className="agreement-sig-name">Irwin Tran — Owner</p>
                <p className="agreement-sig-signed">Signed{htmlAgreement.coachSignedDate ? ` · ${new Date(htmlAgreement.coachSignedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}` : ''}</p>
              </div>
              <div className="agreement-sig-party">
                <p className="agreement-sig-label">Client</p>
                {htmlAgreement.signed ? (
                  <>
                    <p className="agreement-sig-name">{htmlAgreement.signedName}</p>
                    <p className="agreement-sig-signed">Signed · {new Date(htmlAgreement.signedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </>
                ) : (
                  <p className="agreement-sig-pending">Pending signature</p>
                )}
              </div>
            </div>
            <div className="modal-actions" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', justifyContent: 'space-between' }}>
              <button className="btn-download-pdf" onClick={() => printAgreementPdf(htmlAgreement, user.name)}>
                Download PDF
              </button>
              <button className="btn-primary" onClick={() => setShowAgreementModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

    </main>
  );
}

export default App;
